import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface CompletionData {
  totalProjects: number;
  completedProjects: number;
  completionRate: number;
  averageActualTime: number;
  averageEstimatedTime: number;
  timeAccuracy: number;
}

const ProjectCompletionDashboard: React.FC = () => {
  const [completionData, setCompletionData] = useState<CompletionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { get } = useApi();

  useEffect(() => {
    const fetchCompletionData = async () => {
      try {
        setLoading(true);
        const response = await get('/api/admin/kpis/project-completion');
        if (response.success) {
          setCompletionData(response.data);
        } else {
          setError(response.message || 'Failed to fetch completion data');
        }
      } catch (err: any) {
        setError(err.message || 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchCompletionData();
  }, [get]);

  if (loading) return <div className="p-6">Loading completion data...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!completionData) return <div className="p-6 text-gray-600">No completion data available</div>;

  const getCompletionRateColor = (rate: number) => {
    if (rate >= 80) return 'text-green-600';
    if (rate >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getTimeAccuracyColor = (accuracy: number) => {
    if (accuracy >= 0) return 'text-green-600';
    if (accuracy >= -20) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Project Completion Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Projects */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Projects</h3>
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {completionData.totalProjects}
          </div>
          <p className="text-sm text-gray-600">All projects in system</p>
        </div>

        {/* Completed Projects */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Completed Projects</h3>
          <div className="text-3xl font-bold text-green-600 mb-2">
            {completionData.completedProjects}
          </div>
          <p className="text-sm text-gray-600">Successfully completed</p>
        </div>

        {/* Completion Rate */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Completion Rate</h3>
          <div className={`text-3xl font-bold mb-2 ${getCompletionRateColor(completionData.completionRate)}`}>
            {completionData.completionRate.toFixed(1)}%
          </div>
          <p className="text-sm text-gray-600">Success rate</p>
        </div>
      </div>

      {/* Time Analysis */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Average Completion Time */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Completion Time Analysis</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Actual Time:</span>
              <span className="font-semibold text-blue-600">
                {completionData.averageActualTime > 0 
                  ? `${completionData.averageActualTime.toFixed(0)} days`
                  : 'N/A'
                }
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Estimated Time:</span>
              <span className="font-semibold text-green-600">
                {completionData.averageEstimatedTime > 0 
                  ? `${completionData.averageEstimatedTime.toFixed(0)} days`
                  : 'N/A'
                }
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Time Accuracy:</span>
              <span className={`font-semibold ${getTimeAccuracyColor(completionData.timeAccuracy)}`}>
                {completionData.timeAccuracy > 0 ? '+' : ''}{completionData.timeAccuracy.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* Project Status Breakdown */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Status Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Completed:</span>
              <span className="font-semibold text-green-600">
                {completionData.completedProjects} ({completionData.completionRate.toFixed(1)}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">In Progress:</span>
              <span className="font-semibold text-blue-600">
                {completionData.totalProjects - completionData.completedProjects} 
                ({((completionData.totalProjects - completionData.completedProjects) / completionData.totalProjects * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Completion Rate Analysis</h4>
            <p className="text-sm text-gray-600">
              {completionData.completionRate >= 80 
                ? 'Excellent completion rate! Your contractors are performing very well.'
                : completionData.completionRate >= 60
                ? 'Good completion rate. Consider identifying areas for improvement.'
                : 'Completion rate needs improvement. Review project management processes.'
              }
            </p>
          </div>
          <div>
            <h4 className="font-medium text-gray-700 mb-2">Time Estimation Analysis</h4>
            <p className="text-sm text-gray-600">
              {completionData.timeAccuracy >= 0
                ? 'Great time estimation accuracy! Projects are completing on or ahead of schedule.'
                : completionData.timeAccuracy >= -20
                ? 'Time estimation is reasonable. Consider adding buffer time for complex projects.'
                : 'Time estimation needs improvement. Review estimation processes and add more buffer time.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCompletionDashboard;

